import { Disclosure } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import cn from "classnames";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import IconChevronDown from "@/icons/icon-chevron-down";
import IconChevronUp from "@/icons/icon-chevron-up";
import { useCartWithHooks, useCartWithPendingStatus } from "~/hooks/use-cart";
import { getCouponErrorMessage } from "~/utils/coupon-utils";

import Button from "../common/button";
import Icon from "../common/icon";
import IconDiscount from "../common/icons/icon-discount";
import IconEdit from "../common/icons/icon-edit";
import IconTrash from "../common/icons/icon-trash";
import InputField from "../form/input-field";
import styles from "./cart-promo-code.module.scss";

export default function CartPromoCode() {
  const t = useTranslations();
  const { cart, addCouponCode, cancelDiscountOrderLine, couponError, setCouponError } = useCartWithHooks();
  const schema = z.object({ code: z.string().min(1, t("error.fieldRequired")) });
  const form = useForm<Fields>({ resolver: zodResolver(schema) });
  type Fields = z.infer<typeof schema>;
  const formRef = useRef<HTMLFormElement>(null);
  const [infoMessage, setInfoMessage] = useState<string>();
  const currentCoupon = cart?.ShoppingCart?.Lines?.find((line) => line.DiscountCouponCode);

  useEffect(() => {
    if (currentCoupon?.ID) {
      form.clearErrors("code");
      setInfoMessage(undefined);
      // Update form value when coupon is applied (e.g., after page refresh)
      form.setValue("code", currentCoupon.DiscountCouponCode || "");
    }
  }, [currentCoupon, form]);

  const watchPromoCode = form.watch("code");

  // handle async error from AddDiscountCouponToOrder
  useEffect(() => {
    if (couponError) {
      form.setError("code", { message: getCouponErrorMessage(couponError, t, watchPromoCode) });
      setCouponError(undefined);
      (formRef.current?.querySelector("[aria-invalid]") as HTMLElement)?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponError, watchPromoCode, setCouponError]);

  function onSubmit(close: () => void) {
    return async (fields: Fields) => {
      setInfoMessage(undefined);

      if (currentCoupon?.DiscountCouponCode === fields.code) {
        return;
      }

      if (currentCoupon?.ID) {
        await cancelDiscountOrderLine({
          payload: {
            OrderLineID: currentCoupon.ID,
          },
        });
      }

      try {
        const couponResponse = await addCouponCode({
          orderType: 0,
          payload: {
            CouponCode: fields.code,
          },
        });

        if (couponResponse?.DiscountIsApplied) {
          close();
        } else if (couponResponse && !couponResponse.DiscountIsApplied) {
          setInfoMessage(t("error.invalid_coupon_combination"));
        }
      } catch (error) {
        // Error is handled by onError callback in useCartWithHooks
        // which sets couponError and useEffect will display it
      }
    };
  }

  return (
    <div className={styles.container}>
      <Disclosure as="div">
        {({ open, close }) => (
          <>
            <Disclosure.Button className={cn(styles.button, open && styles.openButton)}>
              <span className={styles.headerTitle}>
                <IconDiscount width={24} height={24} />
                <p className={styles.couponTitle}>{t("generic.coupon")}</p>
              </span>
              {currentCoupon ? (
                <span className={styles.edit}>
                  {t("generic.edit")}
                  <Icon icon={IconEdit} aria-hidden width={16} height={16} />
                </span>
              ) : (
                <Icon icon={open ? IconChevronUp : IconChevronDown} aria-hidden width={16} height={16} />
              )}
            </Disclosure.Button>
            {currentCoupon && !open && (
              <div className={styles.summary}>
                <span className={styles.summaryLabel}>{currentCoupon.DiscountCouponCode}</span>

                <button
                  type="button"
                  className={styles.remove}
                  disabled={form.formState.isSubmitting}
                  onClick={async () => {
                    if (currentCoupon.ID) {
                      await cancelDiscountOrderLine({
                        payload: {
                          OrderLineID: currentCoupon.ID,
                        },
                      });
                      form.reset();
                    }
                  }}
                >
                  {t("generic.remove")}
                  <Icon icon={IconTrash} aria-hidden width={16} height={16} />
                </button>
              </div>
            )}
            <Disclosure.Panel static className={styles.panel}>
              <form ref={formRef} className={styles.panelInner} onSubmit={form.handleSubmit(onSubmit(close))}>
                <InputField
                  className={styles.inputContainer}
                  label={t("generic.promoCode")}
                  disabled={form.formState.isSubmitting}
                  {...form.register("code", { required: true })}
                  defaultValue={currentCoupon?.DiscountCouponCode}
                  hideErrorIcon
                  disableButton
                  error={form.formState.errors.code?.message}
                  informativeMessage={infoMessage}
                  successMessage={currentCoupon ? t("generic.codeApplied") : undefined}
                />

                <div className={styles.actions}>
                  <Button
                    variant="secondary"
                    type="button"
                    disabled={form.formState.isSubmitting}
                    onClick={() => {
                      form.reset();
                      setInfoMessage(undefined);
                      close();
                    }}
                  >
                    {t("generic.cancel")}
                  </Button>
                  <Button variant="primary" type="submit" loading={form.formState.isSubmitting}>
                    {t("generic.apply")}
                  </Button>
                </div>
              </form>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
}
